import { z } from 'zod'

/**
 * Voice layer (board card loap): the browser-facing shapes shared by the
 * Worker's provider registry (`worker/voice/registry.ts`) and the SPA's
 * dictation transports (`web/lib/dictation/*`). Which provider is live is
 * decided by env (`VOICE_TTS_PROVIDER`, `VOICE_STT_PROVIDER`) — Soniox by
 * default, ElevenLabs behind the same protocol.
 */

export const VOICE_PROVIDER_IDS = ['soniox', 'elevenlabs'] as const
export const voiceProviderIdSchema = z.enum(VOICE_PROVIDER_IDS)
export type VoiceProviderId = z.infer<typeof voiceProviderIdSchema>

/** Dictation stops by itself past this — a chat message, not a memo. */
export const MAX_DICTATION_SECONDS = 300

/**
 * What `POST /voice/stt/session` hands the browser: everything one dictation
 * session needs to open its own socket straight to the provider, and never
 * the long-lived API key. Soniox mints a single-use temporary key (60 s to
 * connect); ElevenLabs a single-use realtime token.
 */
export const sttRealtimeSessionSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('soniox'),
    /** `wss://stt-rt[.<region>].soniox.com/transcribe-websocket` */
    url: z.string().url(),
    /** Temporary, single-use `snx_temp_` key — sent as WebSocket protocols `['soniox-api-key', key]`. */
    apiKey: z.string().min(1),
    model: z.string().min(1),
    languageHints: z.array(z.string().min(1)),
    /** ISO time the temporary key stops opening sessions. */
    expiresAt: z.string(),
    maxSeconds: z.number().int().positive(),
  }),
  z.object({
    provider: z.literal('elevenlabs'),
    token: z.string().min(1),
    model: z.string().min(1),
    languageCode: z.string().min(1),
    maxSeconds: z.number().int().positive(),
  }),
])
export type SttRealtimeSession = z.infer<typeof sttRealtimeSessionSchema>

/**
 * Uploads that go through async speech-to-text instead of Firecrawl
 * (`worker/pipelines/document-pipeline.ts` convert step). Video containers
 * are transcribed for their audio track.
 */
export const TRANSCRIBABLE_EXTENSIONS: ReadonlySet<string> = new Set([
  '.mp3',
  '.m4a',
  '.wav',
  '.ogg',
  '.oga',
  '.flac',
  '.aac',
  '.amr',
  '.aiff',
  '.webm',
  '.mp4',
])

export function isTranscribableExtension(ext: string): boolean {
  return TRANSCRIBABLE_EXTENSIONS.has(ext.toLowerCase())
}
